import { useCallback, useEffect, useMemo, useState } from 'react'
import { customerApi as api } from '../../services/api'
import { MagnifyingGlassIcon, PlusIcon } from '@heroicons/react/24/outline'
import AgentAddLeadModal from './AgentAddLeadModal'

/**
 * "My Leads" tab inside the agent dashboard. Shows ONLY the leads
 * this agent personally created via /api/agent/leads. The customer
 * admin dashboard sees all of these PLUS bot-captured ones.
 */
const STATUS_BADGES = {
  new: 'bg-blue-50 text-blue-700 ring-blue-600/20',
  contacted: 'bg-amber-50 text-amber-700 ring-amber-600/20',
  qualified: 'bg-purple-50 text-purple-700 ring-purple-600/20',
  converted: 'bg-green-50 text-green-700 ring-green-600/20',
  lost: 'bg-red-50 text-red-700 ring-red-600/20',
}
const STATUSES = ['new', 'contacted', 'qualified', 'converted', 'lost']

export default function AgentMyLeads() {
  const [leads, setLeads] = useState([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [pageSize] = useState(25)
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState('')
  const [stats, setStats] = useState({ total: 0, today: 0, this_week: 0 })
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [selected, setSelected] = useState(null)
  const [addOpen, setAddOpen] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setErr('')
    try {
      const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) })
      if (search.trim()) params.append('search', search.trim())
      if (statusFilter !== 'all') params.append('status', statusFilter)
      const [listRes, statsRes] = await Promise.all([
        api.get(`/agent/leads?${params.toString()}`),
        api.get('/agent/leads/stats/summary'),
      ])
      setLeads(listRes.data.leads || [])
      setTotal(listRes.data.total || 0)
      setStats(statsRes.data || { total: 0, today: 0, this_week: 0 })
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Could not load your leads.')
    } finally {
      setLoading(false)
    }
  }, [page, pageSize, search, statusFilter])

  useEffect(() => { load() }, [load])

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil((total || 0) / pageSize)),
    [total, pageSize]
  )

  const onLeadSaved = () => {
    load()
  }

  return (
    <section className="space-y-5">
      {/* Stats row */}
      <div className="grid grid-cols-3 gap-3">
        <Kpi label="Total leads" value={stats.total} tone="slate" />
        <Kpi label="Today" value={stats.today} tone="indigo" />
        <Kpi label="This week" value={stats.this_week} tone="emerald" />
      </div>

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
        <div className="relative flex-1">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1) }}
            placeholder="Search your leads by name, email, phone, company…"
            className="w-full pl-10 pr-4 py-2.5 text-sm bg-white border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          />
        </div>
        <div className="inline-flex items-center rounded-xl border border-slate-300 bg-white p-1 self-start overflow-x-auto">
          {['all', ...STATUSES].map((s) => (
            <button
              key={s}
              onClick={() => { setStatusFilter(s); setPage(1) }}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors capitalize whitespace-nowrap ${
                statusFilter === s
                  ? 'bg-slate-900 text-white'
                  : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              {s}
            </button>
          ))}
        </div>
        <button
          onClick={() => setAddOpen(true)}
          className="inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium text-white bg-indigo-600 rounded-xl hover:bg-indigo-700 transition-colors self-start sm:self-auto"
        >
          <PlusIcon className="h-4 w-4" />
          Add lead
        </button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center">
            <div className="w-8 h-8 border-2 border-slate-200 border-t-indigo-600 rounded-full animate-spin mx-auto" />
            <p className="text-sm text-slate-500 mt-3">Loading your leads…</p>
          </div>
        ) : err ? (
          <div className="p-12 text-center text-sm text-rose-600">{err}</div>
        ) : leads.length === 0 ? (
          <div className="p-12 text-center">
            <p className="text-sm text-slate-500 mb-3">
              You haven't captured any leads yet.
            </p>
            <button
              onClick={() => setAddOpen(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-indigo-700 bg-indigo-50 rounded-lg hover:bg-indigo-100 transition-colors"
            >
              <PlusIcon className="h-3.5 w-3.5" />
              Add your first lead
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50">
                <tr>
                  <Th>Name</Th>
                  <Th>Contact</Th>
                  <Th>Company</Th>
                  <Th>Status</Th>
                  <Th>Captured</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {leads.map((lead) => (
                  <tr
                    key={lead.id}
                    onClick={() => setSelected(lead)}
                    className="hover:bg-slate-50 cursor-pointer transition-colors"
                  >
                    <td className="px-4 py-3 text-sm font-medium text-slate-900 whitespace-nowrap">
                      {lead.visitor_name || '—'}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600 whitespace-nowrap">
                      <div className="flex flex-col leading-tight">
                        {lead.visitor_email && <span>{lead.visitor_email}</span>}
                        {lead.phone && <span className="text-xs text-slate-400">{lead.phone}</span>}
                        {!lead.visitor_email && !lead.phone && '—'}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600 whitespace-nowrap">
                      {lead.company_name || '—'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset ${STATUS_BADGES[lead.status] || STATUS_BADGES.new}`}>
                        {lead.status || 'new'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-500 whitespace-nowrap">
                      {fmtWhen(lead.created_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {total > pageSize && (
          <div className="flex items-center justify-between border-t border-slate-200 bg-slate-50 px-4 py-3">
            <div className="text-xs text-slate-500">
              Page {page} of {totalPages} · {total} leads
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="px-2.5 py-1 text-xs rounded border border-slate-300 bg-white hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Prev
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="px-2.5 py-1 text-xs rounded border border-slate-300 bg-white hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Detail drawer (right-side) */}
      {selected && (
        <LeadDetailDrawer
          lead={selected}
          onClose={() => setSelected(null)}
          onUpdated={() => { setSelected(null); load() }}
        />
      )}

      <AgentAddLeadModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onSaved={onLeadSaved}
      />
    </section>
  )
}

function Th({ children }) {
  return (
    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider whitespace-nowrap">
      {children}
    </th>
  )
}

function Kpi({ label, value, tone }) {
  const tones = {
    slate: 'from-slate-50 to-slate-100 text-slate-700',
    indigo: 'from-indigo-50 to-indigo-100 text-indigo-700',
    emerald: 'from-emerald-50 to-emerald-100 text-emerald-700',
  }
  return (
    <div className={`bg-gradient-to-br ${tones[tone] || tones.slate} rounded-xl p-4 border border-slate-200`}>
      <div className="text-[11px] font-medium uppercase tracking-wider opacity-70">{label}</div>
      <div className="text-2xl font-bold mt-1">{value}</div>
    </div>
  )
}

function fmtWhen(iso) {
  if (!iso) return '—'
  try {
    const d = new Date(iso)
    const now = new Date()
    const diffMs = now - d
    const diffMin = Math.floor(diffMs / 60000)
    if (diffMin < 1) return 'just now'
    if (diffMin < 60) return `${diffMin}m ago`
    const diffHr = Math.floor(diffMin / 60)
    if (diffHr < 24) return `${diffHr}h ago`
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
      + ' ' + d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
  } catch {
    return iso
  }
}

function LeadDetailDrawer({ lead, onClose, onUpdated }) {
  const [status, setStatus] = useState(lead.status || 'new')
  const [description, setDescription] = useState(lead.description || '')
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState('')

  const save = async () => {
    setSaving(true)
    setErr('')
    try {
      await api.put(`/agent/leads/${lead.id}`, { status, description })
      onUpdated?.()
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Could not update.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-40 flex" onClick={onClose}>
      <div className="flex-1 bg-slate-900/40 backdrop-blur-sm" />
      <div
        className="w-full max-w-md bg-white shadow-2xl flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="text-sm font-semibold text-slate-900">Lead detail</div>
            <div className="text-xs text-slate-500">Captured {fmtWhen(lead.created_at)}</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 p-1 rounded hover:bg-slate-100">
            <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          <ReadRow label="Name" value={lead.visitor_name} />
          <ReadRow label="Email" value={lead.visitor_email} />
          <ReadRow label="Phone" value={lead.phone} />
          <ReadRow label="Company" value={lead.company_name} />
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1.5">Status</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full px-3 py-2 text-sm bg-white border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              {STATUSES.map((s) => (
                <option key={s} value={s} className="capitalize">{s}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1.5">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={5}
              className="w-full px-3 py-2 text-sm bg-white border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-y"
            />
          </div>
          {err && (
            <div className="rounded-lg border border-rose-200 bg-rose-50 text-rose-700 text-xs px-3 py-2">{err}</div>
          )}
        </div>
        <div className="px-6 py-3.5 border-t border-slate-200 bg-slate-50 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-3.5 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-100 disabled:opacity-50 transition-colors"
          >
            Close
          </button>
          <button
            onClick={save}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:bg-slate-300 transition-colors"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  )
}

function ReadRow({ label, value }) {
  return (
    <div>
      <div className="text-xs font-medium text-slate-500">{label}</div>
      <div className="text-sm text-slate-900 mt-0.5">{value || <span className="text-slate-400">—</span>}</div>
    </div>
  )
}
